import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { confirmDialog } from "@/lib/dialogs";
import { ChevronLeft, AlertTriangle, Plus, Trash2, Pencil } from "lucide-react";

const OPS = [
  ["yes", "Is Yes / true"], ["no", "Is No / false"], ["eq", "Equals"], ["ne", "Not equal to"],
  ["gt", "Greater than"], ["lt", "Less than"], ["gte", "Greater than or equal"], ["lte", "Less than or equal"],
  ["contains", "Contains text"], ["in", "In list (comma-separated)"],
];
const NO_VALUE_OPS = new Set(["yes", "no"]);
const OUTCOMES = [
  ["review_required", "Review required (still priced)"],
  ["requires_consultation", "Consultation required (hide price)"],
];
const blank = () => ({
  name: "", active: false, outcome: "review_required", applies_to: "any", match: "all",
  conditions: [{ factor: "", op: "yes", value: "" }], internal_reason: "", public_message: "",
});

export default function PricingTriggers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ["super_admin", "pricing_admin"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [services, setServices] = useState([]);
  const [factorKeys, setFactorKeys] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/pricing-triggers").then((r) => setRows(r.data)).catch(() => toast.error("Could not load triggers"));
  useEffect(() => {
    load();
    api.get("/services").then((r) => {
      const svcs = r.data || [];
      setServices(svcs);
      const keys = new Set();
      svcs.forEach((s) => ((s.pricing_v2?.components) || []).forEach((c) => c.key && keys.add(c.key)));
      setFactorKeys([...keys].sort());
    }).catch(() => {});
  }, []);

  const openNew = () => { setEditId(null); setForm(blank()); setOpen(true); };
  const openEdit = (t) => {
    setEditId(t.id);
    setForm({
      name: t.name || "", active: !!t.active, outcome: t.outcome || "review_required",
      applies_to: t.applies_to || "any", match: t.match || "all",
      conditions: (t.conditions && t.conditions.length ? t.conditions : [{ factor: "", op: "yes", value: "" }]).map((c) => ({ factor: c.factor || "", op: c.op || "yes", value: c.value ?? "" })),
      internal_reason: t.internal_reason || "", public_message: t.public_message || "",
    });
    setOpen(true);
  };

  const setC = (i, k, v) => setForm((f) => ({ ...f, conditions: f.conditions.map((c, j) => (j === i ? { ...c, [k]: v } : c)) }));
  const addC = () => setForm((f) => ({ ...f, conditions: [...f.conditions, { factor: "", op: "yes", value: "" }] }));
  const delC = (i) => setForm((f) => ({ ...f, conditions: f.conditions.filter((_, j) => j !== i) }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Give the trigger a name"); return; }
    const conds = form.conditions.filter((c) => c.factor.trim());
    if (!conds.length) { toast.error("Add at least one condition with a factor"); return; }
    setBusy(true);
    const payload = { ...form, conditions: conds };
    try {
      if (editId) await api.put(`/pricing-triggers/${editId}`, payload);
      else await api.post("/pricing-triggers", payload);
      toast.success(editId ? "Trigger updated" : "Trigger created");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  const toggleActive = async (t) => {
    try { await api.put(`/pricing-triggers/${t.id}`, { ...t, active: !t.active }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const remove = async (t) => {
    if (!(await confirmDialog({ title: "Delete trigger?", description: `“${t.name}” will be permanently removed.`, tone: "destructive", confirmText: "Delete trigger" }))) return;
    try { await api.delete(`/pricing-triggers/${t.id}`); toast.success("Trigger deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const svcName = (id) => (services.find((s) => s.id === id)?.client_name) || id;
  const condText = (c) => {
    const label = (OPS.find((o) => o[0] === c.op) || [])[1] || c.op;
    return NO_VALUE_OPS.has(c.op) ? `${c.factor} — ${label}` : `${c.factor} — ${label} “${c.value}”`;
  };

  return (
    <div>
      <PageHeader title="Pricing Triggers" accent="amber" Icon={AlertTriangle}
        subtitle="Rules that flag a quote for internal review, or require a consultation (hiding the online price) when the engine can't safely model the request."
        actions={<div className="flex gap-2">
          {canEdit && <Button size="sm" className="bg-cass-navy text-white" onClick={openNew} data-testid="pt-new"><Plus className="w-4 h-4 mr-1" />New trigger</Button>}
          <Button variant="outline" size="sm" onClick={() => navigate("/admin")} data-testid="pt-back"><ChevronLeft className="w-4 h-4 mr-1" />Settings</Button>
        </div>} />
      <div className="p-4 md:p-8 max-w-[1000px] space-y-3" data-testid="pt-list">
        {rows.length === 0 && <p className="text-sm text-cass-slate" data-testid="pt-empty">No triggers yet. Create one to flag reviews or require consultations.</p>}
        {rows.map((t) => (
          <Card key={t.id} className="p-4 rounded-xl" data-testid={`pt-row-${t.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-alt font-semibold text-cass-navy truncate">{t.name}</h3>
                  <Badge variant="outline" className={t.outcome === "requires_consultation" ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"} data-testid={`pt-outcome-${t.id}`}>
                    {t.outcome === "requires_consultation" ? "Consultation" : "Review"}
                  </Badge>
                  {!t.active && <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200">Inactive</Badge>}
                </div>
                <div className="text-xs text-cass-slate mt-1 space-y-0.5">
                  <div>Applies to: <span className="text-cass-navy">{t.applies_to === "any" ? "Any service" : svcName(t.applies_to)}</span> · Match: <span className="text-cass-navy">{t.match === "any" ? "any condition" : "all conditions"}</span></div>
                  <ul className="list-disc ml-4">{(t.conditions || []).map((c, i) => <li key={i} className="font-mono text-[11px]">{condText(c)}</li>)}</ul>
                  {t.internal_reason && <div className="italic">Reason: {t.internal_reason}</div>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="flex items-center gap-1.5">
                  <Switch checked={!!t.active} onCheckedChange={() => canEdit && toggleActive(t)} disabled={!canEdit} data-testid={`pt-toggle-${t.id}`} />
                  <span className="text-xs text-cass-slate">{t.active ? "On" : "Off"}</span>
                </div>
                {canEdit && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(t)} data-testid={`pt-edit-${t.id}`}><Pencil className="w-4 h-4" /></Button>}
                {canEdit && <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => remove(t)} data-testid={`pt-del-${t.id}`}><Trash2 className="w-4 h-4" /></Button>}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pt-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit trigger" : "New trigger"}</DialogTitle>
            <DialogDescription>Conditions are checked against the quote answers. “Review” keeps the price but flags it; “Consultation” hides the online price and asks the prospect to book a call.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Overseas entities" data-testid="pt-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Outcome</Label>
                <Select value={form.outcome} onValueChange={(v) => setForm({ ...form, outcome: v })}>
                  <SelectTrigger data-testid="pt-outcome"><SelectValue /></SelectTrigger>
                  <SelectContent>{OUTCOMES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs">Applies to</Label>
                <Select value={form.applies_to} onValueChange={(v) => setForm({ ...form, applies_to: v })}>
                  <SelectTrigger data-testid="pt-applies"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any service</SelectItem>
                    {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.client_name}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            </div>
            <div><Label className="text-xs">Fire when</Label>
              <Select value={form.match} onValueChange={(v) => setForm({ ...form, match: v })}>
                <SelectTrigger data-testid="pt-match"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">ALL conditions match</SelectItem><SelectItem value="any">ANY condition matches</SelectItem></SelectContent>
              </Select></div>

            <div className="border-t border-border pt-3">
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Conditions</Label>
                <Button type="button" size="sm" variant="outline" className="h-7" onClick={addC} data-testid="pt-add-cond"><Plus className="w-3.5 h-3.5 mr-1" />Add condition</Button>
              </div>
              <datalist id="pt-factor-keys">{factorKeys.map((k) => <option key={k} value={k} />)}</datalist>
              {form.conditions.map((c, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end mt-2" data-testid={`pt-cond-${i}`}>
                  <div><Label className="text-[10px]">Factor (answer key)</Label><Input list="pt-factor-keys" value={c.factor} onChange={(e) => setC(i, "factor", e.target.value)} placeholder="e.g. overseas_entities" data-testid={`pt-cond-factor-${i}`} /></div>
                  <div><Label className="text-[10px]">Operator</Label>
                    <Select value={c.op} onValueChange={(v) => setC(i, "op", v)}>
                      <SelectTrigger data-testid={`pt-cond-op-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{OPS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label className="text-[10px]">Value</Label><Input value={c.value} onChange={(e) => setC(i, "value", e.target.value)} disabled={NO_VALUE_OPS.has(c.op)} placeholder={NO_VALUE_OPS.has(c.op) ? "—" : "value"} data-testid={`pt-cond-value-${i}`} /></div>
                  <Button type="button" size="icon" variant="ghost" className="h-9 w-9 text-red-500" onClick={() => delC(i)} data-testid={`pt-cond-del-${i}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>

            <div><Label className="text-xs">Internal reason (staff-only)</Label><Textarea rows={2} value={form.internal_reason} onChange={(e) => setForm({ ...form, internal_reason: e.target.value })} data-testid="pt-internal" /></div>
            {form.outcome === "requires_consultation" && (
              <div><Label className="text-xs">Public message (shown to the prospect instead of a price)</Label><Textarea rows={2} value={form.public_message} onChange={(e) => setForm({ ...form, public_message: e.target.value })} placeholder="Leave blank to use the default consultation message" data-testid="pt-public" /></div>
            )}
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="pt-active" /><span className="text-sm text-cass-slate">Active</span></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-cass-navy text-white" onClick={save} disabled={busy} data-testid="pt-save">{editId ? "Save changes" : "Create trigger"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
